import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AZURE_MAPS_SUBSCRIPTION_KEY,
  getDirectionsUrl,
  loadAzureMapsSdk,
  type Location,
} from '@/components/azure-map/azureMapsSdk'

interface AzureMapProps {
  id?: string
  placeName: string
  width?: string
  height?: string
  zoom?: number
  latitude?: number | null
  longitude?: number | null
}

type AtlasMap = {
  controls: {
    add: (control: unknown, options?: { position?: string }) => void
  }
  events: {
    add: (event: string, callback: () => void) => void
  }
  markers: {
    add: (marker: unknown) => void
    clear: () => void
  }
  dispose: () => void
  setCamera: (options: { center: [number, number]; zoom: number }) => void
}

type AtlasNamespace = {
  control: {
    ZoomControl: new () => unknown
  }
  HtmlMarker: new (options: { color: string; position: [number, number] }) => unknown
  Map: new (
    container: HTMLElement,
    options: {
      authOptions: { authType: 'subscriptionKey'; subscriptionKey: string }
      center: [number, number]
      language: string
      style: string
      view: string
      zoom: number
    },
  ) => AtlasMap
}

const normalizeAddress = (value: string) =>
  value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ')

const geocodeRequests = new Map<string, Promise<Location | null>>()

const geocodeAddress = (address: string, apiKey: string) => {
  const cacheKey = `${apiKey}:${address.toLowerCase()}`
  const cached = geocodeRequests.get(cacheKey)
  if (cached) return cached
  const params = new URLSearchParams({
    'api-version': '1.0',
    query: address,
    limit: '1',
    language: 'it-IT',
    view: 'Auto',
  })
  const request = fetch(`https://atlas.microsoft.com/search/address/json?${params}`, {
    headers: { 'subscription-key': apiKey },
  })
    .then(async (response) => {
      if (!response.ok) throw new Error(`Azure Maps geocoding failed: ${response.status}`)
      const data = (await response.json()) as { results?: Array<{ position?: { lat?: number; lon?: number } }> }
      const position = data.results?.[0]?.position
      return typeof position?.lat === 'number' &&
        typeof position.lon === 'number' &&
        Number.isFinite(position.lat) &&
        Number.isFinite(position.lon)
        ? { lat: position.lat, lon: position.lon }
        : null
    })
    .catch((error) => {
      console.error(error)
      return null
    })
  geocodeRequests.set(cacheKey, request)
  return request
}

const getMarker = (atlas: AtlasNamespace, { lon, lat }: Location) =>
  new atlas.HtmlMarker({ color: '#0b5ed7', position: [lon, lat] })

const AzureMap = ({
  id,
  placeName,
  latitude,
  longitude,
  width = '100%',
  height = '25vh',
  zoom = 15,
}: AzureMapProps) => {
  const address = useMemo(() => normalizeAddress(placeName), [placeName])
  const apiKey = AZURE_MAPS_SUBSCRIPTION_KEY
  const mapElementRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<AtlasMap | null>(null)
  const [mapError, setMapError] = useState(false)
  const [fallback, setFallback] = useState<{ address: string; location: Location | null } | null>(null)
  const hasCoordinates =
    latitude != null && longitude != null && Number.isFinite(latitude) && Number.isFinite(longitude)
  const location = useMemo(() => {
    if (hasCoordinates) return { lat: latitude!, lon: longitude! }
    return fallback?.address === address ? fallback.location : null
  }, [hasCoordinates, latitude, longitude, fallback, address])

  useEffect(() => {
    if (hasCoordinates || !address || !apiKey) return
    let active = true
    geocodeAddress(address, apiKey).then((result) => {
      if (active) setFallback({ address, location: result })
    })
    return () => {
      active = false
    }
  }, [hasCoordinates, address, apiKey])

  useEffect(() => {
    return () => {
      mapRef.current?.dispose()
      mapRef.current = null
    }
  }, [])

  useEffect(() => {
    let active = true

    if (!location) {
      mapRef.current?.dispose()
      mapRef.current = null
      return
    }
    if (!apiKey || !mapElementRef.current) return

    const center: [number, number] = [location.lon, location.lat]
    const currentLocation = location

    loadAzureMapsSdk<AtlasNamespace>()
      .then((atlas) => {
        if (!active || !mapElementRef.current) return

        if (!mapRef.current) {
          const map = new atlas.Map(mapElementRef.current, {
            authOptions: { authType: 'subscriptionKey', subscriptionKey: apiKey },
            center,
            language: 'it-IT',
            style: 'road',
            view: 'Auto',
            zoom,
          })

          map.controls.add(new atlas.control.ZoomControl(), { position: 'top-right' })
          map.events.add('ready', () => {
            if (!active) return
            map.markers.add(getMarker(atlas, currentLocation))
          })
          mapRef.current = map
          return
        }

        mapRef.current.setCamera({ center, zoom })
        mapRef.current.markers.clear()
        mapRef.current.markers.add(getMarker(atlas, currentLocation))
      })
      .catch((error) => {
        console.error(error)
        if (active) setMapError(true)
      })

    return () => {
      active = false
    }
  }, [apiKey, location, zoom])

  if (!apiKey) {
    return (
      <div id={id} style={{ width, height, display: 'grid', placeItems: 'center' }}>
        Azure Maps non configurato
      </div>
    )
  }

  if (!location) {
    return (
      <div id={id} style={{ width, height, display: 'grid', placeItems: 'center' }}>
        {address && fallback?.address !== address ? 'Caricamento mappa...' : 'Posizione non trovata'}
      </div>
    )
  }

  return (
    <div id={id} style={{ width, display: 'grid', gap: 8 }}>
      <button
        type="button"
        onClick={() => {
          window.location.href = getDirectionsUrl(location, address)
        }}
        style={{
          justifySelf: 'start',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '9px 14px',
          border: '1px solid #0a58ca',
          borderRadius: 4,
          background: '#0d6efd',
          color: '#fff',
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        <span className="dx-icon dx-icon-car" aria-hidden="true" />
        Naviga qui
      </button>
      <div
        ref={mapElementRef}
        aria-label={address}
        style={{
          width: '100%',
          height,
          minHeight: 180,
          overflow: 'hidden',
          border: '1px solid #d0d5dd',
          borderRadius: 6,
          background: '#eef2f6',
        }}
      />
      {mapError && <div style={{ color: '#667085' }}>Mappa non disponibile</div>}
    </div>
  )
}

export default AzureMap
